import { useState } from 'react';

import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';

import { motion, useScroll, useTransform } from 'framer-motion';

import AnimateWhileInView from '@/components/shared/AnimateWhileInView';
import { FAQ } from '@/components/shared/lp/developer/FAQ';
import { GuiApplications } from '@/components/shared/lp/developer/GuiApplications';

import KomodoWalletDownloads from '@/data/KomodoWalletDownloads.json';
import { siteData } from '@/data/configs/site';
import { data } from '@/data/reports/q1-2021/charts/websiteTraffic';
import atomIcon from '@/public/images/lp/developer/project-icons/atom.svg';
import avaxIcon from '@/public/images/lp/developer/project-icons/avax.svg';
import bchIcon from '@/public/images/lp/developer/project-icons/bch.svg';
import bnbIcon from '@/public/images/lp/developer/project-icons/bnb.svg';
import browserIcon from '@/public/images/lp/developer/project-icons/browser.svg';
import btcIcon from '@/public/images/lp/developer/project-icons/btc.svg';
import dashIcon from '@/public/images/lp/developer/project-icons/dash.svg';
import desktopIcon from '@/public/images/lp/developer/project-icons/desktop.svg';
import dgbIcon from '@/public/images/lp/developer/project-icons/dgb.svg';
import dogeIcon from '@/public/images/lp/developer/project-icons/doge.svg';
import ethIcon from '@/public/images/lp/developer/project-icons/eth.svg';
import ftmIcon from '@/public/images/lp/developer/project-icons/ftm.svg';
import glassPerson from '@/public/images/lp/developer/project-icons/glassPerson.png';
import gui from '@/public/images/lp/developer/project-icons/gui.png';
import kmdIcon from '@/public/images/lp/developer/project-icons/kmd.svg';
import komodoCube from '@/public/images/lp/developer/project-icons/komodoCube.png';
import linkSvg from '@/public/images/lp/developer/project-icons/link.svg';
import mobileIcon from '@/public/images/lp/developer/project-icons/mobile.svg';
import polIcon from '@/public/images/lp/developer/project-icons/pol.svg';
import webAppIcon from '@/public/images/lp/developer/project-icons/webApp.svg';

export default function Developer() {
  const [selected, setSelected] = useState(null);
  const { scrollY } = useScroll();

  // Parallax effects for background gradients
  const topGradientY = useTransform(scrollY, [0, 1000], [0, 100]);
  const rightGradientX = useTransform(scrollY, [0, 1000], [0, 150]);
  const leftGradientX = useTransform(scrollY, [0, 1000], [0, -150]);
  const bottomGradientY = useTransform(scrollY, [1000, 2000], [0, -100]);
  const bottomRightGradientX = useTransform(scrollY, [1500, 2500], [0, -150]);

  // Gradient animations
  const gradientVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 0.8, transition: { duration: 1.2, ease: 'easeOut' } },
  };

  // Floating animation variants
  const floatingAnimation = {
    initial: { y: -15 },
    animate: {
      y: [15, -15, 15],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatType: 'reverse' as const,
        ease: 'easeInOut',
      },
    },
  };

  const staggeredContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariant = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 200, damping: 20 } },
  };

  const faqData = [
    {
      faqNo: '1',
      faqQstn: 'What is Komodo?',
      faqAnswer: (
        <span>
          Komodo is an open-source technology provider that offers blockchain solutions for
          developers and enterprises. It enables the creation of custom blockchains, DEXs, wallets,
          and more.
          <br />
          <br />
          Check out the{' '}
          <Link
            href='https://github.com/KomodoPlatform'
            target='_blank'
            className='text-[#9B71FF] hover:underline'
          >
            Komodo Platform GitHub Org
          </Link>{' '}
          to discover our active projects and contribute to the ecosystem.
        </span>
      ),
    },
    {
      faqNo: '2',
      faqQstn: 'What is the Komodo SDK, and how do I get started as a developer?',
      faqAnswer: (
        <span>
          Komodo SDK is a complete toolkit for building dApps, multi asset wallets, cross-chain
          cross-protocol DEXes, and more. It consists of three parts: Komodo DeFi Framework, Komodo
          Core Protocol, and Komodo GUI/DAPP Suite.
          <br />
          <br />
          Get started by exploring our{' '}
          <Link
            href='https://komodoplatform.com/en/docs/'
            target='_blank'
            className='text-[#9B71FF] hover:underline'
          >
            developer documentation
          </Link>
          , where you&apos;ll find guides, code examples, and tutorials.
        </span>
      ),
    },
    {
      faqNo: '3',
      faqQstn: 'Can I launch my own blockchain with Komodo SDK?',
      faqAnswer: (
        <span>
          Yes, Komodo Core Protocol enables you to create{' '}
          <Link
            href='https://komodoplatform.com/en/docs/smart-chains/'
            target='_blank'
            className='text-[#9B71FF] hover:underline'
          >
            independent blockchains
          </Link>{' '}
          (Komodo Smart Chains) with custom parameters, consensus rules, and tokenomics. Each Komodo
          Smart Chain has its own native coin, so there&apos;s no need to use KMD for gas fees.
        </span>
      ),
    },
    {
      faqNo: '4',
      faqQstn: 'What types of applications can I build with Komodo DeFi Framework?',
      faqAnswer: (
        <span>
          With{' '}
          <Link
            href='https://komodoplatform.com/en/docs/komodo-defi-framework/'
            target='_blank'
            className='text-[#9B71FF] hover:underline'
          >
            Komodo DeFi Framework
          </Link>
          , you can build non-custodial wallets, cross-chain decentralized exchanges, cross-chain
          bridges, IDO Portals, and more.
        </span>
      ),
    },
    {
      faqNo: '5',
      faqQstn: 'What programming languages can I use?',
      faqAnswer: (
        <span>
          We offer an{' '}
          <Link
            href='https://github.com/KomodoPlatform/komodo-defi-sdk-flutter'
            target='_blank'
            className='text-[#9B71FF] hover:underline'
          >
            SDK for flutter
          </Link>{' '}
          that enables any UI developer familiar with Flutter to easily build and deploy advanced
          blockchain applications.
          <br />
          <br />
          SDKs for additional programming languages are planned for the future. In the meantime, the
          core{' '}
          <Link
            href='https://github.com/KomodoPlatform/komodo-defi-framework'
            target='_blank'
            className='text-[#9B71FF] hover:underline'
          >
            Komodo DeFi Framework
          </Link>{' '}
          is language-agnostic and can be used with any language capable of sending RPC requests.
        </span>
      ),
    },
    {
      faqNo: '6',
      faqQstn: 'Can I deploy smart contracts with Komodo SDK?',
      faqAnswer: (
        <span>
          No, Komodo does not natively support EVM-based smart contracts. However, you can deploy
          specific smart contracts on EVM-compatible chains to enable them to participate in{' '}
          <Link
            href='https://komodoplatform.com/en/docs/start-here/core-technology-discussions/komodo-defi-framework/'
            target='_blank'
            className='text-[#9B71FF] hover:underline'
          >
            cross-chain atomic swaps
          </Link>{' '}
          using Komodo DeFi Framework.
        </span>
      ),
    },
  ];
  return (
    <>
      <main className='relative z-[100] font-sans text-white'>
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #9B71FF 58.86%)',
            filter: 'blur(150px)',
            y: topGradientY,
          }}
          className='absolute left-[-400px] top-[200px] z-[-10] h-[400px] w-[500px] lg:h-[519px] lg:w-[621px] xl:w-[921px] 3xl:-left-[400px] 3xl:top-[200px] 3xl:h-[619px] 3xl:w-[1221px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.3 }}
          variants={gradientVariants}
        ></motion.div>
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #9B71FF 58.86%)',
            filter: 'blur(150px)',
            x: rightGradientX,
          }}
          className='absolute right-[-400px] top-[700px] z-[-10] h-[400px] w-[500px] lg:h-[587px] lg:w-[799px] 3xl:-right-[300px] 3xl:top-[800px] 3xl:h-[619px] 3xl:w-[1221px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.3 }}
          variants={gradientVariants}
        ></motion.div>
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #9B71FF 58.86%)',
            filter: 'blur(72.9px)',
            opacity: 0.7,
            transform: 'rotate(78.79deg)',
          }}
          className='absolute -left-[300px] top-[2200px] z-[-10] h-[400px] w-[500px] md:top-[1800px] lg:left-[-600px] lg:top-[1800px] lg:h-[587px] lg:w-[799px] xl:left-[-700px] xl:top-[1000px] 3xl:-left-[700px] 3xl:top-[1100px] 3xl:h-[619px] 3xl:w-[1221px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        ></motion.div>
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #9B71FF 58.86%)',
            filter: 'blur(150px)',
            y: bottomGradientY,
          }}
          className='absolute bottom-[200px] left-[-400px] z-[-10] h-[400px] w-[500px] lg:h-[519px] lg:w-[621px] xl:w-[921px] 3xl:-left-[400px] 3xl:bottom-[200px] 3xl:h-[619px] 3xl:w-[1221px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        ></motion.div>
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #9B71FF 58.86%)',
            filter: 'blur(150px)',
            x: bottomRightGradientX,
          }}
          className='absolute bottom-[600px] right-[-400px] z-[-10] h-[400px] w-[500px] lg:h-[587px] lg:w-[799px] 3xl:-right-[300px] 3xl:bottom-[600px] 3xl:h-[619px] 3xl:w-[1221px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        ></motion.div>
        <div className='container mx-auto items-center justify-center lg:grid lg:grid-cols-24 lg:gap-0'>
          <div className='hidden lg:col-span-2 lg:block'></div>
          <div className='relative w-full px-4 pt-[100px] sm:px-8 md:mx-auto lg:col-span-20 lg:pt-[150px] xl:px-0'>
            <section className=''>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className='text-center text-[48px] font-normal leading-[110%] tracking-[-2.16px] lg:text-[72px]'
              >
                <h1 className='text-white'>From Vision to Ecosystem</h1>
                <p className='in-hours-text font-bold'>in Hours</p>
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className='mx-auto pt-5 text-center font-sans text-lg font-normal leading-[30px] text-white sm:w-[570px] md:w-[700px] lg:pt-[32px] xl:w-[897px]'
              >
                From custom blockchains to wallet apps, NFTs, and cross-platform atomic swaps,
                Komodo&apos;s all-inclusive platform gives blockchain developers the tools to
                launch, secure, and scale fast. We provide everything you need to shape the future
                of blockchain in one place.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className='mx-auto flex flex-col justify-center gap-[19px] pt-[40px] xs:flex-row -xs:items-center'
              >
                <div className=''>
                  <Link href={siteData.kdfWalkthrough} target='_blank'>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className='h-[44px] text-black -sm:w-[180px]'
                      style={{
                        background: 'linear-gradient(to right, #ffffff 0%, #9e75ff 100%)',
                        borderRadius: '5px',
                      }}
                    >
                      <span className='px-2 font-sans text-sm font-semibold leading-[20px] sm:px-6 sm:text-base'>
                        Start Building
                      </span>
                    </motion.button>
                  </Link>
                </div>
                <div className=''>
                  <Link href={siteData.docsLink} target='_blank'>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className='dev-btn h-[44px] -sm:w-[180px]'
                    >
                      <span className='dev-btn-text px-2 font-sans text-sm font-semibold sm:px-6 sm:text-base'>
                        Documentation
                      </span>
                    </motion.button>
                  </Link>
                </div>
                <div className=''>
                  <Link href={siteData.roadmap} target='_blank'>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className='dev-btn h-[44px] -sm:w-[180px]'
                    >
                      <span className='dev-btn-text px-2 font-sans text-sm font-semibold sm:px-6 sm:text-base'>
                        Explore Roadmap
                      </span>
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            </section>
            <section className='pt-[48px] -sm:px-2'>
              <AnimateWhileInView delay={0.2}>
                <motion.div
                  variants={staggeredContainer}
                  initial='hidden'
                  whileInView='visible'
                  viewport={{ once: true, amount: 0.3 }}
                  className='listed-coins relative mx-auto flex w-full items-center justify-center p-6 sm:w-[570px] md:w-[700px] lg:w-[780px] xl:w-[850px] 2xl:h-[178px] 2xl:w-[1162px]'
                >
                  <div className='absolute left-1/2 top-1/2 z-[8] min-h-[94%] min-w-[99%] -translate-x-1/2 -translate-y-1/2 rounded-[14px] bg-black' />
                  <div className='relative z-10 grid grid-cols-3 items-center justify-center gap-5 md:grid-cols-4 lg:grid-cols-6'>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={bnbIcon} alt='bnb' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={btcIcon} alt='btc' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={ethIcon} alt='eth' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={ftmIcon} alt='ftm' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={bchIcon} alt='bch' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={avaxIcon} alt='avalanche' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={kmdIcon} alt='kmd' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={dogeIcon} alt='doge' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={dashIcon} alt='dash' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={polIcon} alt='matic' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={dgbIcon} alt='dgb' className='-sm:w-[80%]' />
                    </motion.div>
                    <motion.div variants={itemVariant}>
                      <ExportedImage src={atomIcon} alt='cosmos' className='-sm:w-[80%]' />
                    </motion.div>
                  </div>
                </motion.div>
              </AnimateWhileInView>
            </section>
            <section className='mx-auto flex items-center justify-between pt-[48px] text-white lg:w-full 2xl:pt-[80px] -xl:flex-col'>
              <div className='flex w-3/5 flex-col space-y-4 3xl:w-1/2 -xl:mb-12 -xl:w-[80%] -xl:items-center -xl:text-center -sm:w-auto'>
                <div className=''>
                  <AnimateWhileInView delay={0.15}>
                    <h2 className='dev-header-text pt-[21px] font-heading text-2xl font-semibold leading-[120%] tracking-[-1.44px] lg:text-4xl xl:text-5xl'>
                      Komodo SDK & Tools
                    </h2>
                  </AnimateWhileInView>
                  <AnimateWhileInView delay={0.2}>
                    <p className='pt-[21px] font-heading font-semibold leading-[120%] xl:text-[32px] -xl:text-[24px] -sm:text-[14px] -sm:leading-[18px]'>
                      Build an Ecosystem
                    </p>
                  </AnimateWhileInView>
                  <AnimateWhileInView delay={0.25}>
                    <motion.a
                      target='_blank'
                      href='/'
                      className=''
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                    >
                      <p className='max-w-[522px] pt-[16px] font-sans text-[16px] font-medium leading-[23px] xl:pt-[44px]'>
                        Our atomic swaps empower users to trade assets securely across
                        blockchains—without intermediaries. This breakthrough technology is the
                        foundation of our DeFi ecosystem, ensuring trustless, transparent
                        transactions for all.
                      </p>
                    </motion.a>
                  </AnimateWhileInView>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className='flex flex-col gap-x-[15px] pt-[20px] md:flex-row md:gap-x-[30px] -md:gap-y-[15px]'
                >
                  <Link
                    href='https://komodoplatform.com/en/docs/komodo-defi-framework/tutorials/'
                    target='_blank'
                  >
                    <div className='flex flex-row items-center'>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className='contribute-btn flex w-full items-center justify-center gap-[8px] p-2 sm:p-3 md:w-auto xl:px-[20px] xl:py-[13px] -md:px-[20px] -md:py-[13px]'
                      >
                        <span className='font-sans text-sm font-bold leading-[120%] text-white xl:text-base'>
                          Tutorials
                        </span>
                        <ExportedImage src={linkSvg} alt='link' />
                      </motion.button>
                    </div>
                  </Link>
                  <Link
                    href='https://komodoplatform.com/en/docs/komodo-defi-framework/api/v20/'
                    target='_blank'
                  >
                    <div className='flex flex-row items-center'>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className='contribute-btn flex w-full items-center justify-center gap-[8px] p-2 sm:p-3 md:w-auto xl:px-[20px] xl:py-[13px] -md:px-[20px] -md:py-[13px]'
                      >
                        <span className='font-sans text-sm font-bold leading-[120%] text-white xl:text-base'>
                          API References
                        </span>
                        <ExportedImage src={linkSvg} alt='link' />
                      </motion.button>
                    </div>
                  </Link>
                  <Link
                    href='https://github.com/KomodoPlatform/komodo-defi-sdk-flutter/blob/main/README.md'
                    target='_blank'
                  >
                    <div className='flex flex-row items-center'>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className='contribute-btn flex items-center justify-center gap-[8px] p-2 sm:p-3 xl:px-[20px] xl:py-[13px] -md:px-[20px] -md:py-[13px]'
                      >
                        <span className='font-sans text-sm font-bold leading-[120%] text-white xl:text-base'>
                          SDK Documentation
                        </span>
                        <ExportedImage src={linkSvg} className='' alt='link' />
                      </motion.button>
                    </div>
                  </Link>
                </motion.div>
              </div>
              <motion.div
                initial='initial'
                animate='animate'
                variants={floatingAnimation}
                className='flex w-2/5 flex-col space-y-4 3xl:w-1/2 -xl:mb-12 -xl:w-[80%] -xl:items-center -xl:text-center -sm:w-auto'
              >
                <ExportedImage
                  placeholder='empty'
                  src={komodoCube}
                  alt='komodoCube'
                  className='h-auto w-full 2xl:w-3/4'
                />
              </motion.div>
            </section>
            <section className='mx-auto flex items-center pt-[48px] text-white lg:w-full xl:justify-between 2xl:pt-[80px] -xl:flex-col -xl:gap-x-[30px]'>
              <div className='flex w-2/5 flex-col space-y-4 3xl:w-1/2 -xl:order-2 -xl:mb-12 -xl:w-[80%] -xl:items-center -xl:text-center -sm:w-auto'>
                <ExportedImage
                  placeholder='empty'
                  src={gui}
                  alt='GUI'
                  className='h-auto w-full 2xl:w-3/4'
                />
              </div>
              <div className='flex w-1/2 flex-col space-y-4 pt-[68px] 3xl:w-1/2 -xl:order-1 -xl:mb-12 -xl:w-[80%] -xl:items-center -xl:text-center -sm:w-auto'>
                <div className=''>
                  <AnimateWhileInView delay={0.15}>
                    <h2 className='dev-header-text pt-[21px] font-heading text-2xl font-semibold leading-[120%] tracking-[-1.44px] lg:text-4xl xl:text-5xl'>
                      GUI Applications
                    </h2>
                  </AnimateWhileInView>
                  <AnimateWhileInView delay={0.2}>
                    <p className='pt-[21px] font-heading font-semibold leading-[120%] xl:text-[32px] -xl:text-[24px] -sm:text-[14px] -sm:leading-[18px]'>
                      Inspire Your Blockchain Development
                    </p>
                  </AnimateWhileInView>
                  <AnimateWhileInView delay={0.25}>
                    <motion.a
                      target='_blank'
                      href='/'
                      className=''
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                    >
                      <p className='max-w-[591px] pt-[16px] font-sans text-[16px] font-medium leading-[23px] xl:pt-[32px] 2xl:opacity-80'>
                        Komodo Wallet is a non-custodial wallet, cross-chain DEX, and crypto bridge
                        all rolled into one app. Draw inspiration from Komodo Wallet and start
                        Budling your own apps.
                      </p>
                      <p className='pt-[48px] font-sans text-[16px] font-semibold leading-[23px]'>
                        Try Komodo Wallet on any device or OS.
                      </p>
                    </motion.a>
                  </AnimateWhileInView>
                </div>

                <div className='flex w-full flex-col sm:w-[476px] lg:w-[600px] -xl:items-center'>
                  <div className='mx-auto flex w-full flex-col md:flex-row lg:gap-y-[6px] -xl:items-center -xl:justify-around -xl:gap-y-[15px]'>
                    <GuiApplications
                      params={{
                        heading: 'Komodo Web App',
                        img: webAppIcon,
                        alt: 'webApp',
                        position: 'left',
                        href: KomodoWalletDownloads.webApp,
                      }}
                    />
                    <GuiApplications
                      params={{
                        heading: 'Browser Extension',
                        img: browserIcon,
                        alt: 'browser',
                        position: 'right',
                        href: KomodoWalletDownloads.chromeWebStore,
                      }}
                    />
                  </div>
                  <div className='mx-auto flex w-full flex-col pt-[20px] md:flex-row -xl:items-center -xl:justify-around -xl:gap-y-[15px]'>
                    <GuiApplications
                      params={{
                        heading: 'Komodo Mobile',
                        img: mobileIcon,
                        alt: 'mobile',
                        position: 'left',
                        href: '/en/komodo-wallet/mobile/',
                      }}
                    />
                    <GuiApplications
                      params={{
                        heading: 'Komodo Desktop App',
                        img: desktopIcon,
                        alt: 'desktop',
                        position: 'right',
                        href: '/en/komodo-wallet/desktop/',
                      }}
                    />
                  </div>
                </div>
              </div>
            </section>
            <section className='mx-auto flex items-center justify-between pb-[100px] pt-[48px] text-white lg:w-full 2xl:pt-[80px] -xl:flex-col'>
              <div className='flex w-3/5 flex-col space-y-4 3xl:w-1/2 -xl:mb-12 -xl:w-[80%] -xl:items-center -sm:w-auto'>
                <div className=''>
                  <AnimateWhileInView delay={0.15}>
                    <h2 className='dev-header-text pt-[21px] font-heading text-2xl font-semibold leading-[120%] tracking-[-1.44px] lg:text-4xl xl:text-5xl -lg:text-center'>
                      Become A Community Developer
                    </h2>
                  </AnimateWhileInView>
                  <AnimateWhileInView delay={0.2}>
                    <p className='pt-[21px] font-heading font-semibold leading-[120%] xl:text-[32px] -xl:text-[24px] -lg:text-center -sm:text-[14px] -sm:leading-[18px]'>
                      Join our community and make an impact.
                    </p>
                  </AnimateWhileInView>
                  <div className=''>
                    <AnimateWhileInView delay={0.25}>
                      <motion.a
                        target='_blank'
                        href='/'
                        className=''
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                      >
                        <ul className='list-inside list-disc pl-1 pt-[16px] xl:pt-[28px] -sm:pl-5'>
                          <li className='pl-1 font-sans text-base font-medium leading-[23px]'>
                            Find existing issues on GitHub and start contributing.
                          </li>
                          <li className='py-[13px] pl-1 font-sans text-base font-medium leading-[23px]'>
                            Explore opportunities to earn crypto through bounties (when available)
                          </li>
                          <li className='pl-1 font-sans text-base font-medium leading-[23px]'>
                            Propose new features for Komodo Wallet or Komodo DeFi Framework.
                          </li>
                        </ul>
                      </motion.a>
                    </AnimateWhileInView>
                  </div>
                </div>
                <p className='pt-[30px] font-heading text-[17px] font-semibold leading-[120%] text-white'>
                  Your ideas and skills can shape the future of Web3 development.
                </p>
                <div className='flex flex-col gap-x-[15px] pt-[48px] md:flex-row md:gap-x-[30px] -md:gap-y-[15px]'>
                  <Link
                    href='https://github.com/KomodoPlatform/komodo-defi-framework/blob/main/docs/CONTRIBUTING.md'
                    target='_blank'
                  >
                    <div className='flex flex-row items-center'>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className='contribute-btn flex w-full items-center justify-center gap-[8px] p-2 sm:p-3 md:w-auto xl:px-[20px] xl:py-[13px] -md:px-[20px] -md:py-[13px]'
                      >
                        <span className='font-sans text-sm font-bold leading-[120%] text-white xl:text-base'>
                          Contribute
                        </span>
                        <ExportedImage src={linkSvg} alt='link' />
                      </motion.button>
                    </div>
                  </Link>
                  {/* <Link href='/' target='_blank'>
                    <div className='flex flex-row items-center'>
                      <button className='contribute-btn flex w-full items-center justify-center gap-[8px] p-2 sm:p-3 md:w-auto xl:px-[20px] xl:py-[13px] -md:px-[20px] -md:py-[13px]'>
                        <span className='font-sans text-sm font-bold leading-[120%] text-white xl:text-base'>
                          Bounty Program
                        </span>
                        <ExportedImage src={linkSvg} className='' alt='link' />
                      </button>
                    </div>
                  </Link> */}
                  <Link
                    href='https://github.com/KomodoPlatform/komodo-wallet/issues/new/choose'
                    target='_blank'
                  >
                    <div className='flex flex-row items-center'>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className='contribute-btn flex w-full items-center justify-center gap-[8px] p-2 sm:p-3 md:w-auto xl:px-[20px] xl:py-[13px] -md:px-[20px] -md:py-[13px]'
                      >
                        <span className='font-sans text-sm font-bold leading-[120%] text-white xl:text-base'>
                          Suggest Feature
                        </span>
                        <ExportedImage src={linkSvg} alt='link' />
                      </motion.button>
                    </div>
                  </Link>
                </div>
              </div>
              <div className='flex w-2/5 flex-col space-y-4 3xl:w-1/2 -xl:w-[80%] -xl:items-center -xl:text-center -sm:w-auto'>
                <ExportedImage
                  placeholder='empty'
                  src={glassPerson}
                  alt='glassPerson'
                  className='h-auto w-full 2xl:w-3/4'
                />
              </div>
            </section>
            <section className='faq-bg -mt-[100px] pb-[98px] pt-[76px] md:-mt-[100px] lg:-mt-[100px] xl:mt-0'>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className='pb-[40px]'
              >
                <motion.h2
                  initial={{ y: 20 }}
                  whileInView={{ y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className='text-center font-heading text-[36px] font-semibold leading-[44px] tracking-[-0.72px] text-white'
                >
                  Frequently Asked Questions
                </motion.h2>
                <motion.p
                  initial={{ y: 20 }}
                  whileInView={{ y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                  className='text-gray-white-2 pb-[56px] pt-[20px] text-center font-heading text-[20px] font-normal leading-[30px]'
                >
                  Everything you need to know about Komodo
                </motion.p>
                {faqData.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <FAQ
                      faqNo={item.faqNo}
                      faqQstn={item.faqQstn}
                      faqAnswer={item.faqAnswer}
                      className='faq mb-4'
                      className1=''
                      selected={selected}
                      setSelected={setSelected}
                    />
                  </motion.div>
                ))}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className='mx-auto w-[300px] rounded-[16px] bg-[#051019] px-[32px] pb-[40px] pt-[32px] xms:w-[380px] sm:w-[550px] md:w-[650px] lg:w-[750px] xl:w-[950px] 2xl:max-w-[1216px]'
              >
                <h3 className='text-center font-heading text-[20px] font-semibold leading-[30px] text-white'>
                  Still have questions?
                </h3>
                <p className='text-gray-white-2 pt-[8px] text-center font-heading text-[18px] font-normal leading-[28px]'>
                  Can&apos;t find the answer you&apos;re looking for? Please chat to our friendly
                  team.
                </p>
                <div className='mx-auto flex justify-center pt-[32px]'>
                  <Link href='/en/contact/' target='_blank' className=''>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                      className='get-in-touch-btn flex items-center justify-center'
                    >
                      <span className='px-[24px] py-[12px] font-sans text-base font-semibold leading-[20px] text-black'>
                        Get in touch
                      </span>
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            </section>
          </div>
          <div className='hidden lg:col-span-2 lg:block'></div>
        </div>
      </main>
    </>
  );
}
